import { HttpClient, HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';

/*
 */
import { Store } from '@ngrx/store';
import * as ContentAction from '@store/actions/content.actions';
import * as DataAction from '@store/actions/data.actions';
import * as FeaturingActions from '@store/actions/featuring.actions';
import * as MediaGalleryAction from '@store/actions/media-gallery.actions';
import * as ProfileAction from '@store/actions/profile.actions';
import * as UserAction from '@store/actions/users.actions';
import jwtDecode from 'jwt-decode';
import { MetaData, NgEventBus } from 'ng-event-bus';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, catchError, concatMap, filter, Observable, switchMap, take } from 'rxjs';
import { EBEvt } from '../shared/eb-evt';

/*
 */

const COOKIE_EXP_DATE: string | Date = new Date(new Date().setDate(new Date().getDate() + 30));
const DEF_HOST: string = environment.default_host;
const ACCESS_KEY: string = environment.access_token_key;
const REFRESH_KEY: string = environment.refresh_token_key;
const DEF_OPTS = { domain: `${environment.cookie_domain}`, path: '/', expires: COOKIE_EXP_DATE };
const DEF_HEADERS = { 'INTERCONN-KEY': environment.interconn_key }; // Todo

/*
 */
export interface ITokens {
  access: string;
  refresh?: string;
}

/*
 */
@Injectable({
  providedIn: 'root',
})
export class AccessTokenService {
  private evtbus = inject(NgEventBus);
  private http = inject(HttpClient);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private readonly store = inject(Store);

  private token_refreshed$ = new BehaviorSubject<boolean>(false);
  private refreshing = false;
  private skip_refresh = false;

  /*
   */
  constructor() {
    this.init_eb_events();
  }

  /* Init EvtBus events
   */
  private init_eb_events() {
    this.evtbus.on(EBEvt.G_AUTH_SIGNOUT).subscribe((meta: MetaData) => this.force_logout());
  }

  /*
   */
  skipRefresh = (v: boolean) => (this.skip_refresh = v);

  /*
   */
  getAuthString = () => `Bearer ${this.getAccessToken()}`;
  getAuthHeader = () => ({ Authorization: this.getAuthString() });
  getReqWithAuth = (r) => r.clone(this.getAccessToken()?.length ? { setHeaders: this.getAuthHeader() } : {});

  decode = (v: string): any => jwtDecode(v);
  refreshable = () => this.getRefreshToken()?.length;
  getAccessToken = (): string => this.cookieService.get(ACCESS_KEY) || '';
  getRefreshToken = (): string => this.cookieService.get(REFRESH_KEY) || '';

  /*
   */
  setAccessToken = (v: string, opts?: any) => {
    this.cookieService.put(ACCESS_KEY, v, opts ? opts : DEF_OPTS);
    this.evtbus.cast(EBEvt.G_AUTH_TOKEN_ACCESS, v);
  };

  /*
   */
  setRefreshToken = (v: string, opts?: any) => {
    this.cookieService.put(REFRESH_KEY, v, opts ? opts : DEF_OPTS);
    this.evtbus.cast(EBEvt.G_AUTH_TOKEN_REFRESH, v);
  };

  /*
   */
  public clearAuth = () => {
    this.setAccessToken(null);
    this.setRefreshToken(null);
  };

  /* This is tricky way, but we need hard reload after signout
   */
  public force_reload = () => {
    location.reload();
  };

  /*
   */
  public force_logout = () => {
    this.store.dispatch(ProfileAction.userLogout());
    this.store.dispatch(DataAction.clearData());
    this.store.dispatch(ContentAction.clearContent());
    this.store.dispatch(FeaturingActions.clearFeaturing());
    this.store.dispatch(UserAction.clearUser());
    this.store.dispatch(MediaGalleryAction.clear());
    sessionStorage.clear();
    this.clearAuth();
    this.force_reload();
  };

  /*
   */
  public getTokenUser(): any {
    try {
      const token = this.getAccessToken();
      if (!token) {
        return null;
      }
      return jwtDecode<any>(token)?.subject;
    } catch (err) {
      console.error(`@@@@ AccessTokenService.getTokenUser(): Error: `, err);
      return null;
    }
  }

  /*
   */
  public refresh_token(): Observable<any> {
    const refresh = this.getRefreshToken();
    if (!refresh) {
      throw new Error('No refresh token available');
    }
    return this.http.post(`${environment.token_url}/refresh`, { refresh }, { headers: DEF_HEADERS });
  }

  /*
   */
  public process_request(req, next): Observable<any> {
    if (this.refreshing) {
      console.debug('[AccessTokenService] Уже идет обновление токена, ждем завершения. URL:', req.url);
      return this.token_refreshed$.pipe(
        filter((res) => res !== false),
        take(1),
        concatMap(() => {
          console.debug('[AccessTokenService] Обновление токена завершено, повторяем запрос:', req.url);
          return next(this.getReqWithAuth(req));
        }),
      );
    }

    this.refreshing = true;
    this.token_refreshed$.next(false);
    console.warn('[AccessTokenService] Начинаем обновление токена. URL:', req.url);
    return this.refresh_token().pipe(
      switchMap((res: ITokens) => {
        if (res) {
          console.debug(
            '[AccessTokenService] Токен успешно обновлен. Новый access:',
            res.access ? '[OK]' : '[FAIL]',
            'refresh:',
            res.refresh ? '[OK]' : '[FAIL]',
          );
          this.setAccessToken(res.access);
          this.setRefreshToken(res.refresh);
          this.token_refreshed$.next(true);
          this.refreshing = false;
          console.debug('[AccessTokenService] Повторяем исходный запрос с новым токеном:', req.url);
          return next(this.getReqWithAuth(req));
        } else {
          console.error('[AccessTokenService] Ответ на refresh пустой!');
        }
      }),
      catchError((err) => {
        console.error('[AccessTokenService] Ошибка при обновлении токена:', err);
        if (err.status === 410) {
          this.refreshing = false;
          console.debug('[AccessTokenService] Refresh token истек, выполняется logout. URL:', req.url);
          this.force_logout();
          return next(this.getReqWithAuth(req));
        }
        return next(this.getReqWithAuth(req));
      }),
    ) as Observable<HttpEvent<any>>;
  }
}
